import { TasksPriorities } from "src/modules/data-protection/constants/Common";
import {
    INFORMATION_ACCORDIONS,
    buildInformationTabPath,
} from "src/modules/data-protection/pages/admin/software/details/tabs-content/information/helpers/accordionDeepLink";
/** Darker yellow for risk-condition badge text and alert icon (on light yellow surfaces). */
export const RISK_CONDITION_ACCENT_COLOR = "#9a3f0a";

export const FOCUS_MODULE_SLUG = {
    RISK_ASSESSMENT: "risk_assessment",
    DPIA_IMPACT: "data_protection_impact_assessment"
};

/** Internal backend title prefixes stripped for display only. */
const GAP_TITLE_PREFIXES = [
    {
        pattern: /^CE conformity gap:\s*/i,
        kind: "conformity"
    },
    {
        pattern: /^AI Risk Assessment document gap:\s*/i,
        kind: "document"
    },
    {
        pattern: /^AI Risk Assessment compliance gap:\s*/i,
        kind: "compliance"
    },
    {
        pattern: /^Standard AI Act compliance gap:\s*/i,
        kind: "compliance"
    }
];

const SNAKE_CASE_IDENTIFIER = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;

const humanizeSnakeCase = (value) => {
    return value
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const hasGapStatusWording = (value) => {
    return /\bmissing\b/i.test(value) || /\bnot met\b/i.test(value);
};

/**
 * Formats persisted AssessmentGap titles for UI display.
 * Does not mutate backend payloads.
 *
 * @param {string|null|undefined} title
 * @returns {string}
 */
export const formatAssessmentGapDisplayTitle = (title) => {
    if (typeof title !== "string") {
        return "";
    }

    const trimmed = title.trim();
    if (!trimmed) {
        return "";
    }

    let remainder = trimmed;
    let gapKind = null;

    for (const prefix of GAP_TITLE_PREFIXES) {
        if (prefix.pattern.test(remainder)) {
            gapKind = prefix.kind;
            remainder = remainder.replace(prefix.pattern, "").trim();
            break;
        }
    }

    const wasSnakeCase = SNAKE_CASE_IDENTIFIER.test(remainder);
    if (wasSnakeCase) {
        remainder = humanizeSnakeCase(remainder);
    }

    if (gapKind === "document" && !hasGapStatusWording(remainder)) {
        return `${remainder} missing`;
    }

    if (gapKind === "conformity" && wasSnakeCase && !hasGapStatusWording(remainder)) {
        return `${remainder} missing`;
    }

    if (gapKind === "compliance" && !hasGapStatusWording(remainder)) {
        return `${remainder} not met`;
    }

    return remainder;
};

/**
 * @param {{ type?: string, slug?: string, systemData?: string, reportCaseCode?: string, reportCaseId?: number|string }} target
 * @returns {URLSearchParams|null}
 */
export const buildAssessmentQuestionFocusSearchParams = (target) => {
    if (!target || target.type !== "assessment_question") {
        return null;
    }

    const params = new URLSearchParams();
    params.set("focusSystemData", target.systemData || "");

    if (target.questionId != null && target.questionId !== "") {
        params.set("focusQuestionId", String(target.questionId));
    }

    if (target.gapId != null && target.gapId !== "") {
        params.set("focusGapId", String(target.gapId));
    }

    if (target.reportCaseCode) {
        params.set("reportCaseCode", target.reportCaseCode);
    }

    if (target.reportCaseId != null && target.reportCaseId !== "") {
        params.set("reportCaseId", String(target.reportCaseId));
    }

    if (
        target.slug === FOCUS_MODULE_SLUG.RISK_ASSESSMENT
        || target.slug === FOCUS_MODULE_SLUG.DPIA_IMPACT
    ) {
        params.set("focusModuleSlug", target.slug);
    }

    return params;
};

/** Assessment module slugs → user-facing labels for Next Steps gap context. */
export const MODULE_LABEL_BY_SLUG = {
    processing_activity: "Processing Activity",
    data_protection_impact_assessment: "DPIA Impact",
    risk_assessment: "DPIA Risk Assessment",
    fria_assessment: "FRIA",
    conformity_assessment: "CE Conformity",
    ai_risk_assessment: "AI Act",
    ai_risk_assessment_standard: "Standard AI Act"
};

/**
 * @param {{ target?: { slug?: string }, related?: { assessmentSlug?: string } }} row
 * @returns {string|null}
 */
export const resolveAssessmentModuleSlug = (row) => {
    if (!row || typeof row !== "object") {
        return null;
    }

    const targetSlug = row.target && typeof row.target.slug === "string"
        ? row.target.slug.trim()
        : "";
    if (targetSlug) {
        return targetSlug;
    }

    const rawRelatedSlug = row.related?.assessmentSlug ?? row.related?.assessment_slug;
    const relatedSlug = typeof rawRelatedSlug === "string"
        ? rawRelatedSlug.trim()
        : "";

    return relatedSlug || null;
};

/**
 * @param {{ target?: object, related?: object }} row
 * @param {(key: string) => string} t
 * @returns {string|null}
 */
export const resolveRecommendedActionModuleLabel = (row, t) => {
    const slug = resolveAssessmentModuleSlug(row);
    if (!slug) {
        return null;
    }

    const labelKey = MODULE_LABEL_BY_SLUG[slug];
    if (!labelKey) {
        return null;
    }

    return typeof t === "function" ? t(labelKey) : labelKey;
};

/**
 * @param {{ type?: string, target?: object, related?: object }} row
 * @param {(key: string) => string} t
 * @returns {string|null}
 */
export const resolveGapModuleLabel = (row, t) => {
    if (!row || row.type !== "gap") {
        return null;
    }

    return resolveRecommendedActionModuleLabel(row, t);
};

export const RECOMMENDED_ACTION_TYPE_LABELS = {
    task: "Task",
    assessment: "Assessment",
    approval: "Approval",
    documentation: "Documentation",
    obligation: "Obligation",
    gap: "Gap",
    risk: "Risk"
};

/**
 * @param {string} type
 * @param {(key: string) => string} t
 * @param {Record<string, string>} [typeLabels]
 * @returns {string}
 */
export const formatRecommendedActionTypeOnly = (
    type,
    t,
    typeLabels = RECOMMENDED_ACTION_TYPE_LABELS
) => {
    return typeof t === "function"
        ? t(typeLabels[type] || type)
        : (typeLabels[type] || type);
};

/**
 * @param {{ type?: string, title?: string }} row
 * @returns {string}
 */
export const formatRecommendedActionDisplayTitle = (row) => {
    const title = typeof row?.title === "string" ? row.title : "";

    if (row?.type === "gap") {
        return formatAssessmentGapDisplayTitle(title);
    }

    return title;
};

/**
 * @param {{ description?: string }} row
 * @returns {string}
 */
export const formatRecommendedActionDescription = (row) => {
    return typeof row?.description === "string" ? row.description.trim() : "";
};

/**
 * Next Steps only: risk conditions are gaps for the DPIA pre-screen module
 * (`risk_assessment`), resolved the same way as other module labels.
 *
 * @param {object} row
 * @returns {boolean}
 */
export const isRiskAssessmentConditionRow = (row) => {
    return resolveAssessmentModuleSlug(row) === FOCUS_MODULE_SLUG.RISK_ASSESSMENT;
};

/**
 * @param {{ title?: string }} row
 * @returns {string}
 */
export const formatRiskConditionDisplayTitle = (row) => {
    return formatAssessmentGapDisplayTitle(
        typeof row?.title === "string" ? row.title : ""
    );
};

/**
 * @param {{ description?: string }} row
 * @param {(key: string) => string} t
 * @returns {string}
 */
export const formatRiskConditionNextStepsDescription = (row, t) => {
    const moduleLabel = typeof t === "function"
        ? t(MODULE_LABEL_BY_SLUG.risk_assessment)
        : MODULE_LABEL_BY_SLUG.risk_assessment;
    const body = formatRecommendedActionDescription(row);

    if (!body) {
        return moduleLabel;
    }

    return `${moduleLabel}. ${body}`;
};

/**
 * @param {{ total?: number, shown?: number }} counts
 * @returns {boolean}
 */
export const hasTruncatedRecommendedActions = ({ total, shown }) => {
    const shownCount = Number.isFinite(shown) && shown >= 0 ? shown : 0;
    const totalCount = Number.isFinite(total) && total >= 0 ? total : shownCount;

    return totalCount > shownCount;
};

/** Rows rendered at once in the open-items modal. */
export const OPEN_ITEMS_PAGE_SIZE = 5;

/**
 * @param {Array<object>} items
 * @param {number} page 1-based
 * @param {number} [pageSize]
 * @returns {{ items: object[], page: number, pageSize: number, pageCount: number, total: number, start: number, end: number }}
 */
export const paginateRecommendedActionItems = (items, page, pageSize = OPEN_ITEMS_PAGE_SIZE) => {
    const safeItems = Array.isArray(items) ? items : [];
    const size = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : OPEN_ITEMS_PAGE_SIZE;
    const pageCount = Math.max(1, Math.ceil(safeItems.length / size) || 1);
    const requestedPage = Number.isFinite(page) ? page : 1;
    const currentPage = Math.min(Math.max(1, requestedPage), pageCount);
    const startIndex = (currentPage - 1) * size;
    const pageItems = safeItems.slice(startIndex, startIndex + size);

    return {
        items: pageItems,
        page: currentPage,
        pageSize: size,
        pageCount,
        total: safeItems.length,
        start: pageItems.length === 0 ? 0 : startIndex + 1,
        end: startIndex + pageItems.length
    };
};

const ASSESSMENT_CARD_BY_SLUG = {
    processing_activity: "processing-activity",
    risk_assessment: "risk-assessment",
    data_protection_impact_assessment: "dpia",
    ai_risk_assessment: "eu-ai-act",
    ai_risk_assessment_standard: "eu-ai-act",
    fria_assessment: "fria",
    conformity_assessment: "ce-conformity"
};

const buildAssessmentCardLink = (assetId, slug, params) => {
    const assessment = ASSESSMENT_CARD_BY_SLUG[slug];

    if (!assessment) {
        return null;
    }

    const query = params && params.toString()
        ? `&${params.toString()}`
        : "";

    return `/admin/data-protection/softwares/${assetId}?tab=assessments&assessment=${assessment}${query}`;
};

/**
 * @param {string|number} assetId
 * @param {{ type?: string, slug?: string, id?: number|string, systemData?: string, reportCaseCode?: string, reportCaseId?: number|string }} target
 * @returns {string|null}
 */
export const buildRecommendedActionTargetLink = (assetId, target) => {
    if (!target || typeof target !== "object") {
        return null;
    }

    if (target.type === "assessment_question") {
        const params = buildAssessmentQuestionFocusSearchParams(target);

        if (!params) {
            return null;
        }

        return buildAssessmentCardLink(assetId, target.slug, params);
    }

    if (target.type === "assessment" && target.slug) {
        return buildAssessmentCardLink(assetId, target.slug);
    }

    if (target.type === "risk" && target.id) {
        return `/admin/data-protection/softwares/${assetId}?tab=risk-assessment&id=${target.id}`;
    }

    if (target.type === "task" && target.id) {
        return `/admin/data-protection/softwares/${assetId}?tab=task-management&id=${target.id}`;
    }

    if (target.type === "approval") {
        return buildInformationTabPath(
            assetId,
            INFORMATION_ACCORDIONS.REQUIRED_APPROVALS
        );
    }

    return null;
};

/**
 * @param {string} type
 * @param {{ type?: string, target?: object, related?: object }} row
 * @param {(key: string) => string} t
 * @param {Record<string, string>} [typeLabels]
 * @returns {string}
 */
export const formatRecommendedActionTypeLabel = (
    type,
    row,
    t,
    typeLabels = RECOMMENDED_ACTION_TYPE_LABELS
) => {
    if (isRiskAssessmentConditionRow(row)) {
        return typeof t === "function" ? t("Risk Identified") : "Risk Identified";
    }

    const base = typeof t === "function"
        ? t(typeLabels[type] || type)
        : (typeLabels[type] || type);

    const moduleLabel = resolveGapModuleLabel(row, t);
    if (moduleLabel) {
        return `${base} · ${moduleLabel}`;
    }

    return base;
};

/**
 * @param {{ total?: number, shown?: number }} counts
 * @param {(key: string, options?: object) => string} t
 * @returns {string}
 */
export const buildOpenItemsSummaryText = ({ total, shown }, t) => {
    const shownCount = Number.isFinite(shown) && shown >= 0 ? shown : 0;
    const totalCount = Number.isFinite(total) && total >= 0 ? total : shownCount;

    if (totalCount > shownCount) {
        return t("Showing {{shown}} of {{total}} open items", {
            shown: shownCount,
            total: totalCount
        });
    }

    return t("{{count}} open items", { count: totalCount });
};

export const OPEN_ITEMS_MODULE_FILTER_ALL = "__all__";
export const OPEN_ITEMS_MODULE_FILTER_UNASSIGNED = "__unassigned__";
export const OPEN_ITEMS_PRIORITY_FILTER_ALL = "__all__";
export const OPEN_ITEMS_ACTIONABILITY_FILTER_ALL = "__all__";
export const OPEN_ITEMS_SOURCE_FILTER_ALL = "__all__";
export const OPEN_ITEMS_SOURCE_FILTER_UNASSIGNED = "__unassigned__";

const RECOMMENDED_ACTION_ACTIONABILITY_LEVELS = [
    "actionable",
    "waiting",
    "informational"
];

const ACTIONABILITY_LABEL_BY_VALUE = {
    actionable: "Actionable",
    waiting: "Waiting",
    informational: "Informational"
};

const SOURCE_LABEL_BY_DOMAIN = {
    assessment_gap: "Assessment gap",
    documentation: "Documentation"
};

const AI_OBLIGATION_FAMILY_PREFIXES = [
    ["STD-AI-TRANSPARENCY", "Transparency"],
    ["STD-AI-DEPLOYER", "Deployer"],
    ["ARTICLE_50", "Transparency"],
    ["ARTICLE_6", "High-risk classification"],
    ["ARTICLE_5", "Prohibited practices"]
];

const AI_GROUP_MODULE_SLUGS = new Set([
    "ai_risk_assessment",
    "ai_risk_assessment_standard",
    "conformity_assessment",
    "fria_assessment"
]);

const PRIORITY_SORT_RANK = {
    low: 0,
    medium: 1,
    high: 2,
    critical: 3
};

/** Display priority levels for open-items modal filters (matches row badges). */
export const RECOMMENDED_ACTION_PRIORITY_FILTER_LEVELS = [
    "critical",
    "high",
    "medium",
    "low"
];

/**
 * @param {(key: string) => string} t
 * @returns {Array<{ key: string, labelKey: string }>}
 */
export const buildRecommendedActionPriorityFilterOptions = (t) => {
    const options = [{
        key: OPEN_ITEMS_PRIORITY_FILTER_ALL,
        labelKey: "All"
    }];

    RECOMMENDED_ACTION_PRIORITY_FILTER_LEVELS.forEach((level) => {
        options.push({
            key: level,
            labelKey: level
        });
    });

    return options.map((option) => ({
        key: option.key,
        label: typeof t === "function" ? t(option.labelKey) : option.labelKey
    }));
};

/**
 * High, Medium, and Low match software priority everywhere else
 * (`TasksPriorities`, Privacy Risks, Task Management). Critical has no
 * task-priority equivalent, so it takes the darkest red the product already
 * uses for a most-severe level (AI prohibited risk).
 */
const CRITICAL_PRIORITY_COLOR = "#822626";

export const ASSESSMENT_GAP_PRIORITY_COLORS = {
    critical: CRITICAL_PRIORITY_COLOR,
    high: TasksPriorities.high.color,
    medium: TasksPriorities.medium.color,
    low: TasksPriorities.low.color
};

const ASSESSMENT_GAP_PRIORITY_TEXT_COLORS = {
    critical: "#FFFFFF",
    high: "#FFFFFF",
    medium: "#1D1D1D",
    low: "#FFFFFF"
};

const PRIORITY_DISPLAY_LABELS = {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low"
};

/**
 * @param {{ type?: string, priority?: string, related?: { assessmentGapPriority?: string } }} row
 * @returns {string}
 */
export const resolveRecommendedActionDisplayPriority = (row) => {
    if (row?.type === "gap") {
        const gapPriority = row?.related?.assessmentGapPriority;
        if (typeof gapPriority === "string" && gapPriority.trim()) {
            return gapPriority.trim().toLowerCase();
        }
    }

    const priority = typeof row?.priority === "string" ? row.priority.trim().toLowerCase() : "normal";

    if (priority === "normal") {
        return "medium";
    }

    return priority || "medium";
};

/**
 * @param {string} displayPriority
 * @returns {string}
 */
export const getRecommendedActionPriorityBadgeStyle = (displayPriority) => {
    const key = ASSESSMENT_GAP_PRIORITY_COLORS[displayPriority]
        ? displayPriority
        : displayPriority === "normal"
            ? "medium"
            : null;

    if (!key) {
        return {
            backgroundColor: "#6C757D",
            color: "#FFFFFF",
            textTransform: "capitalize"
        };
    }

    return {
        backgroundColor: ASSESSMENT_GAP_PRIORITY_COLORS[key],
        color: ASSESSMENT_GAP_PRIORITY_TEXT_COLORS[key],
        textTransform: "capitalize"
    };
};

export const getRecommendedActionPriorityColor = (displayPriority) => {
    return getRecommendedActionPriorityBadgeStyle(displayPriority).backgroundColor;
};

/**
 * @param {string} displayPriority
 * @param {(key: string) => string} [t]
 * @returns {string}
 */
export const formatRecommendedActionPriorityLabel = (displayPriority, t) => {
    const label = PRIORITY_DISPLAY_LABELS[displayPriority] || displayPriority || "";

    return typeof t === "function" ? t(label) : label;
};

const QUESTION_LINK_TYPES = new Set([
    "assessment_question",
    "documentation",
    "conformity",
    "gap",
    "assessment_gap",
    "documentation_gap"
]);

/**
 * Fills slug and systemData from the row when the action points at a question.
 *
 * @param {object} row
 * @returns {object}
 */
export const resolveRecommendedActionLinkTarget = (row) => {
    if (row?.type === "approval") {
        return {
            ...(row?.target || {}),
            type: "approval",
        };
    }

    const target = { ...(row?.target || {}) };
    const slug = target.slug || row?.related?.assessmentSlug || null;
    const systemData = target.systemData
        || target.system_data
        || row?.related?.systemData
        || row?.related?.system_data
        || row?.source?.code
        || null;
    const questionId = target.questionId
        || target.question
        || row?.related?.questionId
        || null;
    const gapId = target.gapId
        || row?.related?.assessmentGapId
        || row?.related?.assessmentGap?.id
        || null;
    const pointsAtQuestion = QUESTION_LINK_TYPES.has(target.type)
        || QUESTION_LINK_TYPES.has(row?.type)
        || QUESTION_LINK_TYPES.has(row?.source?.domain);

    if (pointsAtQuestion && slug && systemData) {
        return {
            ...target,
            type: "assessment_question",
            slug,
            systemData,
            questionId,
            gapId,
            reportCaseCode: target.reportCaseCode || target.report_case_code || null,
            reportCaseId:
                target.reportCaseId
                ?? target.report_case_id
                ?? null,
        };
    }

    if (systemData && !target.systemData) {
        target.systemData = systemData;
    }

    if (questionId && !target.questionId) {
        target.questionId = questionId;
    }

    if (gapId && !target.gapId) {
        target.gapId = gapId;
    }

    if (slug && !target.slug) {
        target.slug = slug;
    }

    return target;
};

/**
 * Deep links for risk conditions open the Risk Assessment tab and focus the question.
 *
 * @param {object} row
 * @returns {object}
 */
export const resolveRecommendedActionLinkTargetForRow = (row) => {
    const target = resolveRecommendedActionLinkTarget(row);

    if (!isRiskAssessmentConditionRow(row)) {
        return target;
    }

    const systemData = target.systemData
        || row?.related?.systemData
        || row?.related?.system_data
        || null;

    if (!systemData) {
        return {
            ...target,
            type: "assessment",
            slug: FOCUS_MODULE_SLUG.RISK_ASSESSMENT
        };
    }

    return {
        ...target,
        type: "assessment_question",
        slug: FOCUS_MODULE_SLUG.RISK_ASSESSMENT,
        systemData,
        questionId: target.questionId ?? row?.related?.questionId ?? null,
        gapId: target.gapId ?? row?.related?.assessmentGapId ?? null,
        reportCaseCode: target.reportCaseCode || target.report_case_code || null,
        reportCaseId:
            target.reportCaseId
            ?? target.report_case_id
            ?? null
    };
};

/**
 * @param {Array<{ target?: object, related?: object }>} items
 * @param {(key: string) => string} t
 * @returns {Array<{ key: string, label: string, count: number }>}
 */
export const buildRecommendedActionModuleFilterOptions = (items, t) => {
    const safeItems = Array.isArray(items) ? items : [];
    const countsBySlug = new Map();
    let unassignedCount = 0;

    safeItems.forEach((item) => {
        const slug = resolveAssessmentModuleSlug(item);
        if (!slug) {
            unassignedCount += 1;
            return;
        }

        countsBySlug.set(slug, (countsBySlug.get(slug) || 0) + 1);
    });

    const options = [{
        key: OPEN_ITEMS_MODULE_FILTER_ALL,
        label: typeof t === "function" ? t("All") : "All",
        count: safeItems.length
    }];

    Array.from(countsBySlug.keys())
        .sort((slugA, slugB) => {
            const labelA = MODULE_LABEL_BY_SLUG[slugA] || slugA;
            const labelB = MODULE_LABEL_BY_SLUG[slugB] || slugB;

            return labelA.localeCompare(labelB);
        })
        .forEach((slug) => {
            const labelKey = MODULE_LABEL_BY_SLUG[slug] || slug;

            options.push({
                key: slug,
                label: typeof t === "function" ? t(labelKey) : labelKey,
                count: countsBySlug.get(slug) || 0
            });
        });

    if (unassignedCount > 0) {
        options.push({
            key: OPEN_ITEMS_MODULE_FILTER_UNASSIGNED,
            label: typeof t === "function" ? t("Other") : "Other",
            count: unassignedCount
        });
    }

    return options;
};

/**
 * @param {{ target?: object, related?: object }} row
 * @param {string|null|undefined} moduleFilter
 * @returns {boolean}
 */
export const matchesRecommendedActionModuleFilter = (row, moduleFilter) => {
    if (!moduleFilter || moduleFilter === OPEN_ITEMS_MODULE_FILTER_ALL) {
        return true;
    }

    const slug = resolveAssessmentModuleSlug(row);

    if (moduleFilter === OPEN_ITEMS_MODULE_FILTER_UNASSIGNED) {
        return !slug;
    }

    return slug === moduleFilter;
};

/**
 * @param {{ priority?: string }} row
 * @param {string|null|undefined} priorityFilter
 * @returns {boolean}
 */
export const matchesRecommendedActionPriorityFilter = (row, priorityFilter) => {
    if (!priorityFilter || priorityFilter === OPEN_ITEMS_PRIORITY_FILTER_ALL) {
        return true;
    }

    return resolveRecommendedActionDisplayPriority(row) === priorityFilter;
};

/**
 * @param {{ type?: string, title?: string, description?: string, target?: object, related?: object }} row
 * @param {(key: string) => string} t
 * @returns {string}
 */
export const buildRecommendedActionSearchHaystack = (row, t) => {
    const title = isRiskAssessmentConditionRow(row)
        ? formatRiskConditionDisplayTitle(row)
        : formatRecommendedActionDisplayTitle(row);
    const description = isRiskAssessmentConditionRow(row)
        ? formatRiskConditionNextStepsDescription(row, t)
        : formatRecommendedActionDescription(row);

    return [
        title,
        description,
        resolveRecommendedActionModuleLabel(row, t) || "",
        isRiskAssessmentConditionRow(row) ? "Risk Identified" : ""
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
};

/**
 * @param {{ type?: string, title?: string, description?: string, target?: object, related?: object }} row
 * @param {string} searchQuery
 * @param {(key: string) => string} t
 * @returns {boolean}
 */
export const matchesRecommendedActionSearchQuery = (row, searchQuery, t) => {
    const query = typeof searchQuery === "string" ? searchQuery.trim().toLowerCase() : "";

    if (!query) {
        return true;
    }

    return buildRecommendedActionSearchHaystack(row, t).includes(query);
};

/**
 * @param {{ actionability?: string }} row
 * @returns {string|null}
 */
export const resolveRecommendedActionActionability = (row) => {
    const value = typeof row?.actionability === "string"
        ? row.actionability.trim().toLowerCase()
        : "";

    return value || null;
};

/**
 * @param {(key: string) => string} t
 * @param {string} value
 * @returns {string}
 */
const translateActionabilityLabel = (t, value) => {
    const labelKey = ACTIONABILITY_LABEL_BY_VALUE[value] || value;

    return typeof t === "function" ? t(labelKey) : labelKey;
};

/**
 * @param {Array<{ actionability?: string }>} items
 * @param {(key: string) => string} t
 * @returns {Array<{ key: string, label: string, count: number }>}
 */
export const buildRecommendedActionActionabilityFilterOptions = (items, t) => {
    const safeItems = Array.isArray(items) ? items : [];
    const counts = new Map();

    safeItems.forEach((item) => {
        const value = resolveRecommendedActionActionability(item);
        if (!value) {
            return;
        }

        counts.set(value, (counts.get(value) || 0) + 1);
    });

    const keys = [
        ...RECOMMENDED_ACTION_ACTIONABILITY_LEVELS.filter((level) => counts.has(level)),
        ...Array.from(counts.keys()).filter((level) => {
            return !RECOMMENDED_ACTION_ACTIONABILITY_LEVELS.includes(level);
        })
    ];

    return [
        {
            key: OPEN_ITEMS_ACTIONABILITY_FILTER_ALL,
            label: typeof t === "function" ? t("All") : "All",
            count: safeItems.length
        },
        ...keys.map((key) => ({
            key,
            label: translateActionabilityLabel(t, key),
            count: counts.get(key) || 0
        }))
    ];
};

/**
 * @param {{ actionability?: string }} row
 * @param {string|null|undefined} actionabilityFilter
 * @returns {boolean}
 */
export const matchesRecommendedActionActionabilityFilter = (row, actionabilityFilter) => {
    if (!actionabilityFilter || actionabilityFilter === OPEN_ITEMS_ACTIONABILITY_FILTER_ALL) {
        return true;
    }

    return resolveRecommendedActionActionability(row) === actionabilityFilter;
};

/**
 * @param {{ source?: { domain?: string } }} row
 * @returns {string|null}
 */
export const resolveRecommendedActionSourceKey = (row) => {
    const domain = typeof row?.source?.domain === "string"
        ? row.source.domain.trim()
        : "";

    return domain || null;
};

/**
 * @param {(key: string) => string} t
 * @param {string} domain
 * @returns {string}
 */
const translateSourceLabel = (t, domain) => {
    const labelKey = SOURCE_LABEL_BY_DOMAIN[domain] || domain.replace(/_/g, " ");

    return typeof t === "function" ? t(labelKey) : labelKey;
};

/**
 * @param {Array<{ source?: { domain?: string } }>} items
 * @param {(key: string) => string} t
 * @returns {Array<{ key: string, label: string, count: number }>}
 */
export const buildRecommendedActionSourceFilterOptions = (items, t) => {
    const safeItems = Array.isArray(items) ? items : [];
    const counts = new Map();
    let unassignedCount = 0;

    safeItems.forEach((item) => {
        const domain = resolveRecommendedActionSourceKey(item);
        if (!domain) {
            unassignedCount += 1;
            return;
        }

        counts.set(domain, (counts.get(domain) || 0) + 1);
    });

    const options = [{
        key: OPEN_ITEMS_SOURCE_FILTER_ALL,
        label: typeof t === "function" ? t("All") : "All",
        count: safeItems.length
    }];

    Array.from(counts.keys())
        .sort((left, right) => left.localeCompare(right))
        .forEach((domain) => {
            options.push({
                key: domain,
                label: translateSourceLabel(t, domain),
                count: counts.get(domain) || 0
            });
        });

    if (unassignedCount > 0) {
        options.push({
            key: OPEN_ITEMS_SOURCE_FILTER_UNASSIGNED,
            label: typeof t === "function" ? t("Other") : "Other",
            count: unassignedCount
        });
    }

    return options;
};

/**
 * @param {{ source?: { domain?: string } }} row
 * @param {string|null|undefined} sourceFilter
 * @returns {boolean}
 */
export const matchesRecommendedActionSourceFilter = (row, sourceFilter) => {
    if (!sourceFilter || sourceFilter === OPEN_ITEMS_SOURCE_FILTER_ALL) {
        return true;
    }

    const domain = resolveRecommendedActionSourceKey(row);

    if (sourceFilter === OPEN_ITEMS_SOURCE_FILTER_UNASSIGNED) {
        return !domain;
    }

    return domain === sourceFilter;
};

/**
 * @param {Array<object>} items
 * @param {{ moduleFilter?: string, priorityFilter?: string, actionabilityFilter?: string, sourceFilter?: string, searchQuery?: string }} filters
 * @param {(key: string) => string} t
 * @returns {object[]}
 */
export const filterRecommendedActionItems = (items, filters, t) => {
    const safeItems = Array.isArray(items) ? items : [];
    const {
        moduleFilter,
        priorityFilter,
        actionabilityFilter,
        sourceFilter,
        searchQuery
    } = filters || {};

    return safeItems.filter((item) => {
        return matchesRecommendedActionModuleFilter(item, moduleFilter)
            && matchesRecommendedActionPriorityFilter(item, priorityFilter)
            && matchesRecommendedActionActionabilityFilter(item, actionabilityFilter)
            && matchesRecommendedActionSourceFilter(item, sourceFilter)
            && matchesRecommendedActionSearchQuery(item, searchQuery, t);
    });
};

/**
 * AI Act obligation family for grouping. Uses an explicit family when the
 * payload has one, otherwise a known code prefix.
 *
 * @param {{ source?: { code?: string, family?: string }, related?: { obligationFamily?: string, obligationCode?: string } }} row
 * @returns {string|null}
 */
export const resolveRecommendedActionObligationFamily = (row) => {
    const explicitFamily = row?.related?.obligationFamily || row?.source?.family;
    if (typeof explicitFamily === "string" && explicitFamily.trim()) {
        return explicitFamily.trim();
    }

    const code = [row?.source?.code, row?.related?.obligationCode].find((value) => {
        return typeof value === "string" && value.trim();
    });

    if (!code) {
        return null;
    }

    const normalizedCode = code.trim().toUpperCase();
    const matchedPrefix = AI_OBLIGATION_FAMILY_PREFIXES.find(([prefix]) => {
        return normalizedCode.startsWith(prefix);
    });

    return matchedPrefix ? matchedPrefix[1] : null;
};

/**
 * @param {{ type?: string, source?: object, related?: object, target?: object }} row
 * @param {(key: string) => string} t
 * @returns {{ key: string, label: string }}
 */
export const resolveRecommendedActionGroup = (row, t) => {
    const slug = resolveAssessmentModuleSlug(row);
    const family = AI_GROUP_MODULE_SLUGS.has(slug)
        ? resolveRecommendedActionObligationFamily(row)
        : null;

    if (family) {
        return {
            key: `family:${family}`,
            label: typeof t === "function" ? t(family) : family
        };
    }

    const moduleLabel = resolveRecommendedActionModuleLabel(row, t);
    if (moduleLabel && slug) {
        return {
            key: `module:${slug}`,
            label: moduleLabel
        };
    }

    return {
        key: "other",
        label: typeof t === "function" ? t("Other") : "Other"
    };
};

/**
 * @param {Array<object>} items
 * @param {(key: string) => string} t
 * @returns {Array<{ key: string, label: string, items: object[] }>}
 */
export const groupRecommendedActionItems = (items, t) => {
    const safeItems = Array.isArray(items) ? items : [];
    const groups = [];
    const indexByKey = new Map();

    safeItems.forEach((item) => {
        const group = resolveRecommendedActionGroup(item, t);
        const existingIndex = indexByKey.get(group.key);

        if (existingIndex == null) {
            indexByKey.set(group.key, groups.length);
            groups.push({
                key: group.key,
                label: group.label,
                items: [item]
            });
            return;
        }

        groups[existingIndex].items.push(item);
    });

    return groups;
};

const compareText = (left, right) => {
    return String(left || "").localeCompare(String(right || ""), undefined, {
        sensitivity: "base",
        numeric: true
    });
};

/**
 * @param {object} left
 * @param {object} right
 * @param {string} field
 * @param {(key: string) => string} t
 * @returns {number}
 */
export const compareRecommendedActionItems = (left, right, field, t) => {
    if (field === "priority") {
        const leftRank = PRIORITY_SORT_RANK[resolveRecommendedActionDisplayPriority(left)] ?? 1;
        const rightRank = PRIORITY_SORT_RANK[resolveRecommendedActionDisplayPriority(right)] ?? 1;

        return leftRank - rightRank;
    }

    if (field === "module") {
        return compareText(
            resolveRecommendedActionModuleLabel(left, t),
            resolveRecommendedActionModuleLabel(right, t)
        );
    }

    if (field === "title") {
        return compareText(
            formatRecommendedActionDisplayTitle(left),
            formatRecommendedActionDisplayTitle(right)
        );
    }

    if (field === "description") {
        return compareText(
            formatRecommendedActionDescription(left),
            formatRecommendedActionDescription(right)
        );
    }

    if (field === "type") {
        return compareText(left?.type, right?.type);
    }

    if (field === "actionability") {
        return compareText(
            resolveRecommendedActionActionability(left),
            resolveRecommendedActionActionability(right)
        );
    }

    return 0;
};

/**
 * Copies the list. An empty sort keeps the backend order.
 *
 * @param {Array<object>} items
 * @param {{ field?: string|null, order?: "asc"|"desc"|null }} sort
 * @param {(key: string) => string} t
 * @returns {object[]}
 */
export const sortRecommendedActionItems = (items, sort, t) => {
    const safeItems = Array.isArray(items) ? items : [];
    const field = sort?.field;
    const order = sort?.order;

    if (!field || (order !== "asc" && order !== "desc")) {
        return safeItems;
    }

    const direction = order === "desc" ? -1 : 1;

    return safeItems
        .map((item, index) => ({ item, index }))
        .sort((left, right) => {
            const compared = compareRecommendedActionItems(left.item, right.item, field, t);
            if (compared === 0) {
                return left.index - right.index;
            }

            return compared * direction;
        })
        .map((entry) => entry.item);
};

/**
 * @param {string} field
 * @param {{ field?: string|null, order?: "asc"|"desc"|null }} current
 * @returns {{ field: string|null, order: "asc"|"desc"|null }}
 */
export const cycleRecommendedActionSort = (field, current) => {
    if (current?.field !== field) {
        return { field, order: "asc" };
    }

    if (current.order === "asc") {
        return { field, order: "desc" };
    }

    return { field: null, order: null };
};
